import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Send, Loader2, RefreshCw } from 'lucide-react';
import { apiClient as api } from '@/services/api-client';

const WhatsAppPairingPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session') || '';
  const phone = searchParams.get('phone') || '';

  const [pairingStatus, setPairingStatus] = useState<'disconnected' | 'connecting' | 'qrcode' | 'connected'>('disconnected');
  const [qrCode, setQrCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [pairedPhone, setPairedPhone] = useState('');

  // Auto-detect direction based on language (default Arabic RTL)
  const isRTL = true;

  const handleStartPairing = async () => {
    if (!sessionId) {
      setErrorMessage('معرف الجلسة غير صالح أو مفقود. يرجى إعادة مسح رمز الاستجابة السريعة.');
      setPairingStatus('disconnected');
      return;
    }

    setLoading(true);
    setErrorMessage('');
    try {
      // Send pairing request to backend
      const response = await api.post(`/api/whatsapp/pair/${sessionId}`, {
        phone: phone,
      });

      if (response.data && response.data.success !== false) {
        setPairingStatus('connecting');
      } else {
        throw new Error(response.data?.message || 'فشل في تهيئة الاقتران');
      }
    } catch (err: any) {
      console.error('Pairing error:', err);
      setErrorMessage(
        err.response?.data?.message || err.message || 'حدث خطأ أثناء الاتصال بالخادم. يرجى المحاولة مرة أخرى.'
      );
      setPairingStatus('disconnected');
    } finally {
      setLoading(false);
    }
  };

  // Automatically start pairing on page load
  useEffect(() => {
    if (sessionId) {
      handleStartPairing();
    } else {
      setErrorMessage('معرف الجلسة غير صالح أو مفقود. يرجى إعادة مسح رمز الاستجابة السريعة.');
    }
  }, [sessionId]);

  // Poll status from server
  useEffect(() => {
    if (!sessionId || pairingStatus === 'connected') return;

    const interval = setInterval(async () => {
      try {
        const response = await api.get(`/api/whatsapp/pairing-status/${sessionId}`);
        const data = response.data;
        if (data) {
          if (data.status) {
            setPairingStatus(data.status);
          }
          if (data.qr) {
            setQrCode(data.qr);
          }
          if (data.phone) {
            setPairedPhone(data.phone);
          }
        }
      } catch (err) {
        console.error('Failed to fetch pairing status:', err);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [sessionId, pairingStatus]);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at center, #0f172a 0%, #020617 100%)',
        color: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        boxSizing: 'border-box',
        direction: isRTL ? 'rtl' : 'ltr',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '440px',
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '24px',
          padding: '32px 24px',
          boxSizing: 'border-box',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5)',
          textAlign: 'center',
        }}
      >
        {/* Logo Icon */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 20px rgba(37, 211, 102, 0.25)',
              position: 'relative',
            }}
          >
            <Send size={32} color="#fff" style={{ transform: isRTL ? 'rotate(180deg)' : 'none' }} />
            {(loading || pairingStatus === 'connecting') && (
              <div
                style={{
                  position: 'absolute',
                  inset: '-4px',
                  borderRadius: '24px',
                  border: '2px solid #25D366',
                  animation: 'pulse 1.5s infinite',
                }}
              />
            )}
          </div>
        </div>

        <style>{`
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            100% { transform: scale(1.15); opacity: 0; }
          }
          @keyframes slideUp {
            from { transform: translateY(12px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
        `}</style>

        {/* Dynamic Content based on State */}
        {errorMessage && (
          <div style={{ animation: 'slideUp 0.3s ease-out' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <AlertTriangle size={48} color="#ef4444" />
            </div>
            <h1 style={{ fontSize: '20px', fontWeight: 800, margin: '0 0 12px 0', color: '#f87171' }}>
              فشل في عملية الربط
            </h1>
            <p style={{ fontSize: '14px', color: '#94a3b8', lineHeight: '1.6', margin: '0 0 24px 0' }}>
              {errorMessage}
            </p>
            <button
              onClick={handleStartPairing}
              style={{
                width: '100%',
                background: 'rgba(255, 255, 255, 0.08)',
                color: '#cbd5e1',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '12px',
                padding: '12px 20px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
            >
              إعادة المحاولة
            </button>
          </div>
        )}

        {!errorMessage && pairingStatus === 'disconnected' && (
          <div style={{ animation: 'slideUp 0.3s ease-out' }}>
            <h1 style={{ fontSize: '20px', fontWeight: 800, margin: '0 0 12px 0', color: '#f8fafc' }}>
              تأكيد ربط الواتساب
            </h1>
            <p style={{ fontSize: '14px', color: '#94a3b8', lineHeight: '1.6', margin: '0 0 24px 0' }}>
              أنت على وشك ربط هذا الهاتف ببوابة مقاولي FTTH Nexus لتلقي إشعارات المشتركين التلقائية.
            </p>
            <button
              onClick={handleStartPairing}
              style={{
                width: '100%',
                background: 'linear-gradient(90deg, #25D366, #128C7E)',
                color: '#fff',
                border: 'none',
                borderRadius: '14px',
                padding: '14px 20px',
                fontSize: '16px',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(37, 211, 102, 0.2)',
              }}
            >
              بدء عملية الاقتران الآن
            </button>
          </div>
        )}

        {!errorMessage && pairingStatus === 'connecting' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0', animation: 'slideUp 0.3s ease-out' }}>
            <Loader2 className="animate-spin" size={40} color="#25D366" style={{ marginBottom: '16px' }} />
            <h2 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 8px 0', color: '#f8fafc' }}>
              جاري إنشاء جلسة الاتصال...
            </h2>
            <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>
              يتم طلب رمز الـ QR الحقيقي مباشرة من السيرفر، يرجى الانتظار للحظات.
            </p>
          </div>
        )}

        {!errorMessage && pairingStatus === 'qrcode' && (
          <div style={{ animation: 'slideUp 0.3s ease-out' }}>
            <h1 style={{ fontSize: '20px', fontWeight: 800, margin: '0 0 8px 0', color: '#f8fafc' }}>
              امسح رمز الاستجابة السريعة (QR Code)
            </h1>
            <p style={{ fontSize: '13px', color: '#94a3b8', lineHeight: '1.5', margin: '0 0 20px 0' }}>
              افتح تطبيق واتساب على هاتفك 🡨 الأجهزة المرتبطة 🡨 ربط جهاز، ثم وجه الكاميرا نحو الشاشة للربط.
            </p>

            <div
              style={{
                background: '#fff',
                padding: '16px',
                borderRadius: '20px',
                display: 'inline-block',
                boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                marginBottom: '20px',
              }}
            >
              {qrCode ? (
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(qrCode)}`}
                  alt="WhatsApp QR Code"
                  style={{ width: '240px', height: '240px', display: 'block' }}
                />
              ) : (
                <div style={{ width: '240px', height: '240px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0f172a' }}>
                  <RefreshCw className="animate-spin" size={32} />
                </div>
              )}
            </div>

            <div
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '12px',
                padding: '12px',
                fontSize: '12px',
                color: '#38bdf8',
                fontWeight: 600,
              }}
            >
              جاري فحص حالة الاتصال تلقائياً فور مسح الرمز...
            </div>
          </div>
        )}

        {!errorMessage && pairingStatus === 'connected' && (
          <div style={{ animation: 'slideUp 0.3s ease-out' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <CheckCircle2 size={48} color="#25D366" />
            </div>
            <h1 style={{ fontSize: '22px', fontWeight: 800, margin: '0 0 12px 0', color: '#25D366' }}>
              تم ربط الواتساب بنجاح!
            </h1>
            <p style={{ fontSize: '14px', color: '#94a3b8', lineHeight: '1.6', margin: '0 0 24px 0' }}>
              تم ربط الرقم الخاص بك ({pairedPhone || phone || 'المسجل'}) بلوحة مقاولي FTTH Nexus بنجاح. يمكنك الآن إغلاق هذه الصفحة.
            </p>
            <div
              style={{
                background: 'rgba(37, 211, 102, 0.06)',
                border: '1px solid rgba(37, 211, 102, 0.15)',
                borderRadius: '12px',
                padding: '12px',
                color: '#25D366',
                fontSize: '13px',
                fontWeight: 600,
              }}
            >
              ✓ الواتساب متصل الآن وبحالة نشطة
            </div>
          </div>
        )}

        {/* Footer info */}
        <div
          style={{
            marginTop: '32px',
            paddingTop: '20px',
            borderTop: '1px solid rgba(255, 255, 255, 0.06)',
            color: '#475569',
            fontSize: '11px',
          }}
        >
          All Rights Reserved City Telecom. By Haider Ali
        </div>
      </div>
    </div>
  );
};

export default WhatsAppPairingPage;
